interface ElementInternals {
    reportValidity(): boolean;
    checkValidity(): boolean;
    setValidity(flags?: ValidityStateFlags | undefined, message?: string | undefined, anchor?: HTMLElement | undefined): void
}

interface ValidityStateFlags {
    badInput?: boolean;
    customError?: boolean;
    patternMismatch?: boolean;
    rangeOverflow?: boolean;
    rangeUnderflow?: boolean;
    stepMismatch?: boolean;
    tooLong?: boolean;
    tooShort?: boolean;
    typeMismatch?: boolean;
    valueMissing?: boolean;
}


class IPSlider extends HTMLElement {
    #internals : ElementInternals;
    #shadow : ShadowRoot;

    constructor() {
        super();
        this.#shadow = this.attachShadow({ mode: 'open' });
        this.#internals = this.attachInternals();
    }

    static get formAssociated() {
        return true;
    }

    static get observedAttributes() {
        return ['step', 'value', 'min', 'max', 'required', 'slide-color', 'hide-value-holder', 'auto-apply-limits'];
    }

    connectedCallback() : void {
        this.#internals.ariaValueNow = this.value;
        this.#internals.ariaValueMin = this.min;
        this.#internals.ariaValueMax = this.max;
        this.#internals.ariaRequired = this.required;

        this.#Render();

        this.#attachEvents();
        this.#updateBackgroundColor(this.#shadow.querySelector('.slider')!, null);
    }


    // #region getters and setters

    get sliderHeight() : string {
        return this.getAttribute('slider-height') ?? '5px';
    }

    set sliderHeiught(value : string | null) {
        this.setAttribute('slider-height', value ?? '5px');
    }

    get valueHolderWidth() : string {
        return this.getAttribute('value-holder-width') ?? '55px';
    }

    set valueHolderWidth(value : string | null) {
        this.setAttribute('value-holder-width', value ?? '55px');
    }

    get valueHolderHeight() : string {
        return this.getAttribute('value-holder-height') ?? '55px';
    }

    set valueHolderHeight(value : string | null) {
        this.setAttribute('value-holder-height', value ?? '55px');
    }

    get overflowErrMsg() :string {
        return this.getAttribute('overflow-err-msg') ?? 'Værdien er for høj';
    }

    set overflowErrMsg(value : string | null) {
        this.setAttribute('overflow-err-msg', value ?? 'Værdien er for høj');
    }

    get underflowErrMsg() : string {
        return this.getAttribute('underflow-err-msg') ?? 'Værdien er for lav';
    }

    set underflowErrMsg(value : string | null) {
        this.setAttribute('underflow-err-msg', value ?? 'Værdien er for lav');
    }

    get invalidErrMsg() : string {
        return this.getAttribute('invalid-err-msg') ?? 'Værdien er ikke gyldig';
    }

    set invalidErrMsg(value : string | null) {
        this.setAttribute('invalid-err-msg', value ?? 'Værdien er ikke gyldig');
    }

    get checkValidity() : boolean {
        return this.#internals.checkValidity();
    }

    get autoApplyLimits() : boolean {
        return this.getAttribute('auto-apply-limits')?.toLowerCase() === 'true';
    }

    set autoApplyLimits(value : boolean) {
        this.setAttribute('auto-apply-limits', value?.toString() ?? '');
    }

    get hideValueHolder() : boolean {
        return this.getAttribute('hide-value-holder')?.toLowerCase() === 'true';
    }

    set hideValueHolder(value : boolean) {
        this.setAttribute('hide-value-holder', value?.toString() ?? '');
    }

    get slideColor() {
        const defaultColor = '#00b200';
        return this.getAttribute('slide-color') ?? defaultColor;
    }

    set slideColor(value : string) {
        this.setAttribute('slide-color', value ?? '');
    }

    get step() {
        return this.getAttribute('step');
    }

    set step(value : string | null) {
        this.setAttribute('step', value ?? '1');
    }

    get value() {
        return this.getAttribute('value');
    }

    set value(value : string | null) {
        this.setAttribute('value', value ?? '');
        this.#internals.setFormValue(value);
    }

    get min() {
        return this.getAttribute('min');
    }

    set min(value : string | null) {
        this.setAttribute('min', value ?? '');
        this.#internals.ariaValueMin = value;
    }

    get max() {
        return this.getAttribute('max');
    }

    set max(value : string | null) {
        this.setAttribute('max', value ?? '');
        this.#internals.ariaValueMax = value;
    }

    get required() {
        return this.getAttribute('required');
    }

    set required(value : string | null) {
        this.setAttribute('required', value ?? '');
        this.#internals.ariaRequired = value;
    }
    // #endregion

    #updateSliderValue(id : string) : void {
        const isSlider = id === 'slider';
        const slider = this.#shadow.querySelector<HTMLInputElement>('#slider')!;
        const sliderValueHolder = this.#shadow.querySelector<HTMLInputElement>('#sliderValue')!;
        const value = isSlider ? slider?.value : sliderValueHolder?.value;

        this.value = value?.toString();
        this.#setValdiation(value);

        this.#updateBackgroundColor(slider, value);

        // sync slider and value holder
        if (isSlider) {
            sliderValueHolder.value = value.toString();
        } else if (value) {
            slider.value = value.toString();
        }
        this.#internals.ariaValueNow = value;

        if (this.autoApplyLimits){
            this.#applyNumberFieldLimits(sliderValueHolder);
        }
    }

    #applyNumberFieldLimits(elm : HTMLInputElement) : void {
        const value = parseInt(elm.value, 10);
        const min = parseInt(elm.min, 10);
        const max = parseInt(elm.max, 10);

        const isSmallerThanMin = value < min;
        const isLargerThanMax = value > max;

        if (isSmallerThanMin) {
            elm.value = elm.min;
            this.value = elm.min;
            this.ariaValueNow = elm.min;
        }
        if (isLargerThanMax) {
            elm.value = elm.max;
            this.value = elm.min;
            this.ariaValueNow = elm.min;
        }
        if (!value) {
            elm.value = elm.min;
            this.value = elm.min;
            this.ariaValueNow = elm.min;
        }
    }

    #attachEvents() : void {
        const slider = this.#shadow.querySelector<HTMLInputElement>('#slider')!;
        const sliderValueHolder = this.#shadow.querySelector<HTMLInputElement>('#sliderValue')!;

        slider.addEventListener('input', () => {
            this.#updateSliderValue(slider.id);
            this.dispatchEvent(new Event('input'));
        });

        slider.addEventListener('change', () => {
            this.dispatchEvent(new Event('change'));
        });

        if (sliderValueHolder.style.display !== 'none') {
            sliderValueHolder.addEventListener('change', () => {
                this.#updateSliderValue(sliderValueHolder.id);
            });
        }
    }

    #updateBackgroundColor(elm : HTMLInputElement, value : string | null) : void {
        const min = parseInt(elm.min, 10);
        const max = parseInt(elm.max, 10);
        const _value = value ? parseInt(value, 10) : parseInt(elm.value, 10);

        const percentage = ((_value - min) / (max - min)) * 100;

        const backgroundColor = `linear-gradient(to right, ${this.slideColor} 0%, ${this.slideColor} ${percentage}%, #ccc ${percentage}%, #ccc 100%)`;

        // Apply the background color to the slider
        elm.style.background = backgroundColor;
    }

    #setValdiation( newValue : string | null) : void {
        const min = parseInt(this.min ?? '', 10);
        const max = parseInt(this.max ?? '', 10);
        const value = parseInt(newValue ?? '', 10);

        if (isNaN(value)) {
            this.#internals.setValidity({valueMissing: true}, this.invalidErrMsg);
        } else if (!isNaN(min) && value < min) {
            this.#internals.setValidity({rangeUnderflow: true}, this.underflowErrMsg);
        } else if (!isNaN(max) && value > max ) {
            this.#internals.setValidity({rangeOverflow: true}, this.overflowErrMsg);
        } else {
            this.#internals.setValidity({valueMissing: false, rangeOverflow: false, rangeUnderflow: false}, '');
        }
        this.#internals.reportValidity();
    }

    #Render() : void {
        const html = `
                    <input class="slider" type="range" min="${this.min}" max="${this.max}" value="${this.#internals.ariaValueNow}" step='${this.step}' ${this.#internals.ariaRequired ? "required='true'" : ''} id="slider" >
                    <input ${this.hideValueHolder ? 'hidden' : ''} class="slider-label" type="number" min="${this.#internals.ariaValueMin}"
                     max="${this.#internals.ariaValueMax}" value="${this.#internals.ariaValueNow}" step='${this.step}' ${this.#internals.ariaRequired ? "required='true'" : ''} id="sliderValue">
                    `;

        const style = `
                <style>
                    .slider {
                        width: 100%;
                        height: ${this.sliderHeight};
                        border-radius: 25px;
                        background: #ccc;
                        cursor: pointer;
                        align-self: center;
                        appearance: none;
                    }

                    .slider-label {
                        margin-left: 10px;
                        width: ${this.valueHolderWidth};
                        height: 25px;
                        align-self: center;
                        border: 1px solid grey;
                        text-align: right;
                        border-radius: 2px;
                    }
                    .slider-label:invalid {
                        margin-left: 10px;
                        width: ${this.valueHolderWidth};
                        height: 25px;
                        align-self: center;
                        border: 2px solid red;
                        border-radius: 2px;
                    }
                    .slider-label:focus {
                        margin-left: 10px;
                        width: ${this.valueHolderWidth};
                        height: 25px;
                        align-self: center;
                        border: 1px solid black;
                        border-radius: 2px;
                    }

                    .slider::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        height: 22px;
                        width: 22px;
                        border: 6px solid #ffffff;
                        border-radius: 100%;
                        background: #efefef;
                        cursor: pointer;
                        position: relative;
                        filter: drop-shadow(0px 0px 5px rgba(0,0,0,0.2));
                    }
                    .slider::-moz-range-thumb {
                        height: 10px;
                        width: 10px;
                        border: 6px solid #ffffff;
                        border-radius: 100%;
                        background: #efefef;
                        cursor: pointer;
                        position: relative;
                        filter: drop-shadow(0px 0px 5px rgba(0,0,0,0.2));
                    }
                </style>`;




        // Create an element inside the shadow DOM
        const div = document.createElement('div');
        div.style.display = 'flex';
        div.style.alignContent = 'center';
        div.style.width = '100%';
        div.innerHTML = html + style;
        this.#shadow.appendChild(div);
    }

}
customElements.define('ip-slider', IPSlider);
